import { ArrowUpRight, Check, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/shared/lib/cn';
import type { SemanticTone } from '@/shared/ui/core/types';
import { ensureUniqueKpiTones, orderKpisWithTotalFirst, resolveKpiTone } from '@/shared/ui/core/statusTone';

export { ensureUniqueKpiTones, orderKpisWithTotalFirst, resolveKpiTone };

export type KpiVariant = 'soft' | 'solid' | 'surface';

export type KpiItem = {
  id: string;
  label: string;
  value: string | number;
  unit?: string;
  hint?: string;
  tone?: SemanticTone;
  variant?: KpiVariant;
  icon?: LucideIcon;
  badge?: string;
  to?: string;
  /** Prefer for list filter actions (avoids full navigation). */
  onClick?: () => void;
  /** Current list filter matches this card. */
  selected?: boolean;
};

const toneValFg: Record<SemanticTone, string> = {
  neutral: 'text-text',
  primary: 'text-primary',
  accent: 'text-success-fg',
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  danger: 'text-danger-fg',
  info: 'text-info-fg',
  hold: 'text-danger-fg',
  draft: 'text-text',
};

const toneSubFg: Record<SemanticTone, string> = {
  neutral: 'text-text-secondary',
  primary: 'text-primary',
  accent: 'text-success-fg',
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  danger: 'text-danger-fg',
  info: 'text-info-fg',
  hold: 'text-danger-fg',
  draft: 'text-text-muted',
};

const toneIconFg: Record<SemanticTone, string> = {
  neutral: 'text-text-secondary',
  primary: 'text-primary',
  accent: 'text-success-fg',
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  danger: 'text-danger-fg',
  info: 'text-info-fg',
  hold: 'text-danger-fg',
  draft: 'text-text-muted',
};

const toneCardUnselected: Record<SemanticTone, string> = {
  neutral: 'border-border/70 bg-bg/50 hover:bg-bg/80 hover:border-border-strong',
  primary: 'border-primary/25 bg-primary-soft/25 hover:bg-primary-soft/40 hover:border-primary/40',
  accent: 'border-success/25 bg-success-soft/25 hover:bg-success-soft/40 hover:border-success/40',
  success: 'border-success/25 bg-success-soft/25 hover:bg-success-soft/40 hover:border-success/40',
  warning: 'border-warning/25 bg-warning-soft/25 hover:bg-warning-soft/40 hover:border-warning/40',
  danger: 'border-danger/25 bg-danger-soft/25 hover:bg-danger-soft/40 hover:border-danger/40',
  info: 'border-info/30 bg-info-soft/40 hover:bg-info-soft/70 hover:border-info/50',
  hold: 'border-danger/25 bg-danger-soft/25 hover:bg-danger-soft/40 hover:border-danger/40',
  draft: 'border-border bg-bg/60 hover:bg-bg/90 hover:border-border-strong',
};

const toneCardSelected: Record<SemanticTone, string> = {
  neutral: 'border-text-secondary/60 bg-border/60 shadow-xs',
  primary: 'border-primary bg-primary-soft shadow-xs',
  accent: 'border-success bg-success-soft shadow-xs',
  success: 'border-success bg-success-soft shadow-xs',
  warning: 'border-warning bg-warning-soft shadow-xs',
  danger: 'border-danger bg-danger-soft shadow-xs',
  info: 'border-info bg-info-soft shadow-xs',
  hold: 'border-danger bg-danger-soft shadow-xs',
  draft: 'border-border-strong bg-surface-raised shadow-xs',
};

const solidCardUnselected: Record<SemanticTone, string> = {
  neutral: 'bg-surface border border-border text-text hover:bg-surface-raised hover:border-border-strong shadow-xs',
  primary: 'bg-primary text-on-primary hover:opacity-95 shadow-xs',
  accent: 'bg-primary text-on-primary hover:opacity-95 shadow-xs',
  success: 'bg-primary text-on-primary hover:opacity-95 shadow-xs',
  warning: 'bg-kpi-warning-bg text-kpi-warning-fg hover:bg-kpi-warning-hover shadow-xs',
  danger: 'bg-kpi-danger-bg text-kpi-danger-fg hover:bg-kpi-danger-hover shadow-xs',
  info: 'bg-kpi-info-bg text-kpi-info-fg hover:bg-kpi-info-hover shadow-xs',
  hold: 'bg-kpi-danger-bg text-kpi-danger-fg hover:bg-kpi-danger-hover shadow-xs',
  draft: 'bg-kpi-draft-bg border border-kpi-draft-border text-kpi-draft-fg hover:bg-kpi-draft-hover shadow-xs',
};

const solidCardSelected: Record<SemanticTone, string> = {
  neutral: 'bg-surface border border-border-strong text-text shadow-xl shadow-slate-900/15 -translate-y-1 scale-[1.02] z-10',
  primary: 'bg-primary text-on-primary shadow-xl shadow-emerald-950/35 -translate-y-1 scale-[1.02] z-10',
  accent: 'bg-primary text-on-primary shadow-xl shadow-emerald-950/35 -translate-y-1 scale-[1.02] z-10',
  success: 'bg-primary text-on-primary shadow-xl shadow-emerald-950/35 -translate-y-1 scale-[1.02] z-10',
  warning: 'bg-kpi-warning-bg text-kpi-warning-fg shadow-xl shadow-amber-950/25 -translate-y-1 scale-[1.02] z-10',
  danger: 'bg-kpi-danger-bg text-kpi-danger-fg shadow-xl shadow-red-950/25 -translate-y-1 scale-[1.02] z-10',
  info: 'bg-kpi-info-bg text-kpi-info-fg shadow-xl shadow-blue-950/25 -translate-y-1 scale-[1.02] z-10',
  hold: 'bg-kpi-danger-bg text-kpi-danger-fg shadow-xl shadow-red-950/25 -translate-y-1 scale-[1.02] z-10',
  draft: 'bg-kpi-draft-bg border border-kpi-draft-border text-kpi-draft-fg shadow-xl shadow-slate-900/15 -translate-y-1 scale-[1.02] z-10',
};

const toneDotBg: Record<SemanticTone, string> = {
  neutral: 'bg-text-secondary',
  primary: 'bg-primary',
  accent: 'bg-primary',
  success: 'bg-primary',
  warning: 'bg-kpi-warning-bg',
  danger: 'bg-kpi-danger-bg',
  info: 'bg-kpi-info-bg',
  hold: 'bg-kpi-danger-bg',
  draft: 'bg-kpi-draft-dot',
};

type KpiSurfaceToneStyle = {
  title: string;
  value: string;
  iconBox: string;
  badge: string;
  borderSelected: string;
  ringSelected: string;
  accentBar: string;
};

const SURFACE_TONE_STYLES: Record<SemanticTone, KpiSurfaceToneStyle> = {
  neutral: {
    title: 'text-text-secondary',
    value: 'text-text',
    iconBox: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    badge: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    borderSelected: 'border-slate-700 dark:border-slate-300',
    ringSelected: 'ring-2 ring-slate-500/20',
    accentBar: 'bg-slate-600',
  },
  primary: {
    title: 'text-text-secondary',
    value: 'text-primary',
    iconBox: 'bg-primary-soft text-primary',
    badge: 'bg-primary-soft text-primary',
    borderSelected: 'border-primary',
    ringSelected: 'ring-2 ring-primary/20',
    accentBar: 'bg-primary',
  },
  accent: {
    title: 'text-text-secondary',
    value: 'text-primary',
    iconBox: 'bg-primary-soft text-primary',
    badge: 'bg-primary-soft text-primary',
    borderSelected: 'border-primary',
    ringSelected: 'ring-2 ring-primary/20',
    accentBar: 'bg-primary',
  },
  success: {
    title: 'text-text-secondary',
    value: 'text-primary',
    iconBox: 'bg-success-soft text-success',
    badge: 'bg-success-soft text-success',
    borderSelected: 'border-success',
    ringSelected: 'ring-2 ring-success/20',
    accentBar: 'bg-success',
  },
  warning: {
    title: 'text-text-secondary',
    value: 'text-warning-fg',
    iconBox: 'bg-warning-soft text-warning',
    badge: 'bg-warning-soft text-warning',
    borderSelected: 'border-warning',
    ringSelected: 'ring-2 ring-warning/20',
    accentBar: 'bg-warning',
  },
  danger: {
    title: 'text-text-secondary',
    value: 'text-danger',
    iconBox: 'bg-danger-soft text-danger',
    badge: 'bg-danger-soft text-danger',
    borderSelected: 'border-danger',
    ringSelected: 'ring-2 ring-danger/20',
    accentBar: 'bg-danger',
  },
  info: {
    title: 'text-text-secondary',
    value: 'text-info-fg',
    iconBox: 'bg-info-soft text-info',
    badge: 'bg-info-soft text-info',
    borderSelected: 'border-info',
    ringSelected: 'ring-2 ring-info/20',
    accentBar: 'bg-info',
  },
  hold: {
    title: 'text-text-secondary',
    value: 'text-danger',
    iconBox: 'bg-danger-soft text-danger',
    badge: 'bg-danger-soft text-danger',
    borderSelected: 'border-danger',
    ringSelected: 'ring-2 ring-danger/20',
    accentBar: 'bg-danger',
  },
  draft: {
    title: 'text-text-secondary',
    value: 'text-text',
    iconBox: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    borderSelected: 'border-slate-500',
    ringSelected: 'ring-2 ring-slate-400/20',
    accentBar: 'bg-slate-500',
  },
};

export function KpiCard({
  item,
  dense = false,
  variant,
  className,
}: {
  item: KpiItem;
  dense?: boolean;
  variant?: KpiVariant;
  className?: string;
}) {
  const tone = item.tone === 'warning' ? 'warning' : resolveKpiTone(item);
  const resolvedVariant: KpiVariant = variant ?? item.variant ?? 'solid';
  const isSolid = resolvedVariant === 'solid';
  const isSurface = resolvedVariant === 'surface';
  const interactive = Boolean(item.to || item.onClick);
  const Icon = item.icon;

  const styles = SURFACE_TONE_STYLES[tone] ?? SURFACE_TONE_STYLES.neutral;

  const body = isSurface ? (
    <div
      className={cn(
        'group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border bg-surface text-left transition-all duration-150',
        dense ? 'p-3 min-h-[84px]' : 'p-3.5 min-h-[96px]',
        item.selected
          ? cn('border-2 shadow-xs -translate-y-0.5', styles.borderSelected, styles.ringSelected)
          : 'border-border/80 shadow-2xs hover:border-primary/40 hover:shadow-xs',
        interactive && 'cursor-pointer active:scale-[0.99]',
        className,
      )}
    >
      {/* Top Header: Uppercase Label + Soft Badge / Icon (không dùng truncate gây mất chữ) */}
      <div className="flex items-start justify-between gap-1.5">
        <div className="flex min-w-0 flex-1 items-start gap-1.5">
          {item.selected ? (
            <span
              className={cn(
                'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full text-white',
                styles.accentBar,
              )}
              aria-hidden
            >
              <Check className="size-2.5 stroke-[3]" />
            </span>
          ) : null}
          <span
            className={cn(
              'text-[11px] font-bold uppercase leading-snug tracking-wide sm:text-xs',
              item.selected ? 'text-text' : styles.title,
            )}
          >
            {item.label}
          </span>
        </div>

        {item.badge ? (
          <span
            className={cn(
              'shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums',
              styles.badge,
            )}
          >
            {item.badge}
          </span>
        ) : Icon ? (
          <span
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-lg transition-transform duration-150 group-hover:scale-105 sm:size-6.5',
              styles.iconBox,
            )}
            aria-hidden
          >
            <Icon className="size-3.5" />
          </span>
        ) : null}
      </div>

      {/* Main Value + Optional Unit */}
      <div className={cn('flex flex-wrap items-baseline gap-1.5', dense ? 'mt-2' : 'mt-2.5')}>
        <span
          className={cn(
            'font-mono font-extrabold tracking-tight tabular-nums leading-none',
            dense ? 'text-2xl sm:text-[26px]' : 'text-2xl sm:text-3xl',
            styles.value,
          )}
        >
          {item.value}
        </span>
        {item.unit ? (
          <span className="whitespace-nowrap text-xs font-medium text-text-muted">
            {item.unit}
          </span>
        ) : null}
      </div>

      {/* Footer Hint + Interactive Arrow */}
      {item.hint ? (
        <div className="mt-2.5 flex items-center justify-between gap-1.5 border-t border-border/50 pt-2 text-[11px]">
          <span className="min-w-0 leading-snug font-medium text-text-secondary">
            {item.hint}
          </span>
          {interactive ? (
            <ArrowUpRight
              className="size-3.5 shrink-0 text-text-muted transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary"
              aria-hidden
            />
          ) : null}
        </div>
      ) : null}
    </div>
  ) : (
    <div
      className={cn(
        'relative h-full flex flex-col justify-between overflow-hidden rounded-control shadow-xs transition-all duration-150',
        dense ? 'px-3 py-2.5 min-h-[64px]' : 'p-3.5 min-h-[76px]',
        isSolid
          ? item.selected
            ? solidCardSelected[tone]
            : solidCardUnselected[tone]
          : cn(
            'border',
            item.selected ? toneCardSelected[tone] : toneCardUnselected[tone],
          ),
        interactive && 'cursor-pointer active:scale-[0.98]',
        interactive && !item.selected && 'hover:shadow-1',
        className,
      )}
    >
      <div>
        <div
          className={cn(
            'flex items-center justify-between gap-1.5 text-[16px]',
            isSolid
              ? tone === 'neutral'
                ? 'text-slate-500'
                : tone === 'draft'
                  ? 'text-slate-600'
                  : 'text-white/80'
              : 'text-text-muted',
          )}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            {item.selected ? (
              <span
                className={cn(
                  'flex items-center justify-center size-3.5 rounded-full shrink-0 shadow-xs',
                  isSolid
                    ? tone === 'neutral'
                      ? 'bg-sky-600 text-white'
                      : tone === 'draft'
                        ? 'bg-slate-700 text-white'
                        : 'bg-white text-slate-900'
                    : toneDotBg[tone],
                )}
                aria-hidden
              >
                <Check className="size-2.5 stroke-[3]" />
              </span>
            ) : null}
            <span
              className={cn(
                'truncate',
                isSolid
                  ? tone === 'neutral'
                    ? 'font-medium text-slate-500'
                    : tone === 'draft'
                      ? 'font-semibold text-slate-700'
                      : 'font-semibold text-white'
                  : item.selected
                    ? 'font-bold text-text'
                    : 'font-normal text-text-secondary',
              )}
            >
              {item.label}
            </span>
          </div>
          {item.badge ? (
            <span
              className={cn(
                'shrink-0 rounded-badge px-2 py-0.5 text-caption font-bold shadow-2xs',
                isSolid
                  ? tone === 'neutral'
                    ? 'bg-sky-50 text-sky-700 border border-sky-200'
                    : tone === 'draft'
                      ? 'bg-white text-slate-700 border border-slate-300'
                      : 'bg-white/20 text-white border border-white/30 backdrop-blur-xs'
                  : 'bg-primary-soft text-primary',
              )}
            >
              {item.badge}
            </span>
          ) : Icon ? (
            <Icon
              className={cn(
                'size-4 shrink-0 transition-transform duration-150',
                isSolid
                  ? tone === 'neutral'
                    ? 'text-sky-600'
                    : tone === 'draft'
                      ? 'text-slate-600'
                      : 'text-white'
                  : toneIconFg[tone],
                item.selected && 'scale-110',
              )}
              aria-hidden
            />
          ) : null}
        </div>
        <div
          className={cn(
            'flex items-baseline justify-between gap-2',
            dense ? 'mt-1' : 'mt-2',
          )}
        >
          <div
            className={cn(
              'tabular-nums leading-tight',
              dense ? 'text-2xl font-black tracking-tight' : 'text-page-title font-bold',
              item.selected ? 'scale-[1.02]' : '',
              isSolid
                ? tone === 'neutral'
                  ? 'text-slate-900 font-extrabold'
                  : tone === 'draft'
                    ? 'text-slate-900 font-black'
                    : 'text-white font-extrabold'
                : toneValFg[tone],
            )}
          >
            {item.value}
          </div>
          {item.hint ? (
            <span
              className={cn(
                'text-caption truncate text-right font-medium',
                isSolid
                  ? tone === 'neutral'
                    ? 'text-slate-500'
                    : tone === 'draft'
                      ? 'text-slate-600'
                      : 'text-white/85'
                  : toneSubFg[tone],
              )}
              title={typeof item.hint === 'string' ? item.hint : undefined}
            >
              {item.hint}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );

  if (item.onClick) {
    return (
      <button
        type="button"
        className="block h-full w-full min-w-0 cursor-pointer border-0 bg-transparent p-0 text-left"
        aria-pressed={item.selected || undefined}
        onClick={item.onClick}
      >
        {body}
      </button>
    );
  }

  if (item.to) {
    return (
      <Link to={item.to} className="block h-full min-w-0 no-underline">
        {body}
      </Link>
    );
  }
  return body;
}

export function KpiGrid({
  items,
  dense = false,
  compact = false,
  variant = 'solid',
  className,
}: {
  items: readonly KpiItem[];
  dense?: boolean;
  /** Same card height as the default grid, fixed width so many buckets do not stretch. */
  compact?: boolean;
  variant?: KpiVariant;
  className?: string;
}) {
  if (items.length === 0) return null;
  const orderedItems = ensureUniqueKpiTones(items);

  if (compact && !dense) {
    return (
      <div className="flex flex-wrap gap-3 pt-1 pb-1">
        {orderedItems.map((item) => (
          <div key={item.id} className="w-[calc(50%-0.375rem)] sm:w-36">
            <KpiCard item={item} variant={variant} />
          </div>
        ))}
      </div>
    );
  }
  if (dense) {
    return (
      <div className="flex flex-wrap gap-2 pt-1 pb-1">
        {orderedItems.map((item) => (
          <div key={item.id} className="w-[calc(50%-0.25rem)] sm:w-36">
            <KpiCard item={item} dense variant={variant} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-3 sm:flex sm:items-stretch sm:flex-wrap pt-1 pb-1',
        className,
      )}
    >
      {orderedItems.map((item) => (
        <div
          key={item.id}
          className={cn(
            'min-w-0 flex flex-col',
            orderedItems.length === 1
              ? 'w-full sm:w-52 sm:flex-none'
              : orderedItems.length === 2
                ? 'flex-1 sm:max-w-xs'
                : 'flex-1',
          )}
        >
          <KpiCard item={item} variant={variant} />
        </div>
      ))}
    </div>
  );
}
